<template>
  <div>
    <calendar-business-processes v-if="isBusinessProcesses" @back="isBusinessProcesses = false"/>
    <template v-else>
      <event-edit-modal :all-event-types="allEventTypes"/>
      <event-info-modal :statuses-info="statusesInfo"/>
      <div class="d-flex flex-row justify-content-between align-items-center pl-1 w-100">
        <div class="d-flex flex-row justify-content-center align-items-center">
          <mdb-dropdown multi-level style="max-width: 500px;">
            <mdb-dropdown-toggle
              rounded
              size="sm"
              slot="toggle"
              gradient="aqua">Filter Event Statuses</mdb-dropdown-toggle>
            <mdb-dropdown-menu style="width: max-content;">
              <ul class="list-unstyled">
                <li
                  v-for="(eventStatus, index) in statusesInfo"
                  :key="`eventTypes-checkbox-${index}`"
                >
                  <mdb-input
                    class="small"
                    type="checkbox"
                    :id="`eventStatuses-checkbox-${index}`"
                    :name="`eventStatuses-checkbox-${index}`"
                    :label="eventStatus.name"
                    v-model="selectedEventStatuses[eventStatus.code]"
                  />
                </li>
                <hr />
                <li>
                  <mdb-btn
                    size="sm"
                    rounded
                    gradient="aqua"
                    @click="getEvents()"
                  >Use Filter</mdb-btn>
                </li>
              </ul>
            </mdb-dropdown-menu>
          </mdb-dropdown>
          <mdb-dropdown multi-level class="ml-3" style="max-width: 500px;">
            <mdb-dropdown-toggle
              rounded
              size="sm"
              slot="toggle"
              gradient="aqua">Filter Event Types</mdb-dropdown-toggle>
            <mdb-dropdown-menu style="width: max-content;">
              <ul class="list-unstyled">
                <li
                  v-for="(eventType, index) in allEventTypes"
                  :key="`eventTypes-checkbox-${index}`"
                >
                  <mdb-input
                    class="small"
                    type="checkbox"
                    :id="`eventTypes-checkbox-${index}`"
                    :name="`eventTypes-checkbox-${index}`"
                    :label="eventType.name"
                    v-model="selectedEventTypes[eventType.code]"
                  />
                </li>
                <hr />
                <li>
                  <mdb-btn
                    size="sm"
                    rounded
                    gradient="aqua"
                    @click="getEvents()"
                  >Use Filter</mdb-btn>
                </li>
              </ul>
            </mdb-dropdown-menu>
          </mdb-dropdown>
          <div class="ml-3 mb-4">
            <label for="deadline-before-datetime">Deadline before...</label>
            <a class="ml-4" @click="deadlineBefore = ''"><mdb-icon icon="times"/></a>
            <br/>
            <datetime
              type="datetime"
              title="Deadline before..."
              input-id="deadline-before-datetime"
              format="yyyy-MM-dd HH:mm"
              v-model="deadlineBefore"
            />
          </div>
        </div>
        <div class="d-flex flex-row justify-content-between align-items-center">
          <mdb-switch
            class="mt-2"
            size="lg"
            color="cyan"
            off-label="Grid"
            on-label="Timeline"
            :checked="isTimeline"
            @getValue="switchTimeline($event)"
          />
          <mdb-btn
            class="ml-3"
            rounded
            gradient="aqua"
            size="sm"
            @click="isBusinessProcesses = true"
          >Edit Businness Process</mdb-btn>
        </div>
      </div>
      <full-calendar
        ref="fullCalendar"
        :options="calendarOptions"
      />
    </template>
  </div>
</template>

<script>
import { mdbSwitch, mdbDropdown, mdbDropdownToggle, mdbDropdownMenu, mdbInput, mdbBtn, mdbIcon } from 'mdbvue'
import { mapActions } from 'vuex'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import {errorCatch} from '../utils'
import EventEditModal from './EventEditModal'
import EventInfoModal from './EventInfoModal'
import {Datetime} from 'vue-datetime'
import CalendarBusinessProcesses from './CalendarBusinessProcesses'

export default {
  name: 'Calendar',
  data () {
    return {
      isTimeline: false,
      isBusinessProcesses: false,
      allEventTypes: [],
      eventTypesLoaded: false,
      selectedEventTypes: {},
      selectedEventStatuses: {},
      deadlineBefore: null,
      events: [],
      statusesInfo: [],
      statusesInfoLoaded: false,
      timelineToGrid: Object.freeze({
        'timelineYear': 'dayGridMonth',
        'timelineMonth': 'dayGridMonth',
        'timelineWeek': 'timeGridWeek',
        'timelineDay': 'timeGridDay'
      }),
      gridToTimeline: Object.freeze({
        'dayGridMonth': 'timelineMonth',
        'timeGridWeek': 'timelineWeek',
        'timeGridDay': 'timelineDay'
      })
    }
  },
  computed: {
    api () { return this.$refs.fullCalendar.getApi() },
    miscDataLoaded () {
      return this.eventTypesLoaded && this.statusesInfoLoaded
    },
    calendarOptions: {
      get: function () {
        return {
          events: this.events,
          eventClick: this.eventClick,
          defaultTimedEventDuration: '00:30',
          firstDay: 1,
          timeZone: 'UTC',
          eventTimeFormat: {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            meridiem: false
          },
          plugins: [ dayGridPlugin, timeGridPlugin, resourceTimelinePlugin, bootstrapPlugin ],
          themeSystem: 'bootstrap',
          initialView: 'dayGridMonth',
          nowIndicator: true,
          customButtons: {
            create: {
              text: 'Create Event',
              click: this.eventCreate
            },
            refresh: {
              text: 'Refresh',
              click: this.refreshClick
            }
          },
          bootstrapFontAwesome: {
            refresh: 'fa-redo-alt'
          },
          headerToolbar: {
            left: 'refresh prev,next today create',
            center: 'title',
            right: this.isTimeline ? 'timelineYear timelineMonth timelineWeek timelineDay' : 'dayGridMonth timeGridWeek timeGridDay'
          },
          datesSet: this.datesSet
        }
      }
    }
  },
  methods: {
    ...mapActions(['apiGetEventTypes', 'apiGetEvents', 'apiEventCreate', 'apiGetEventStatusesInfo', 'apiEventSetStatus']),
    switchTimeline (value) {
      this.isTimeline = value
      const oldView = this.api.view.type
      const newView = this.isTimeline ? this.gridToTimeline[oldView] : this.timelineToGrid[oldView]
      this.api.changeView(newView)
    },
    eventClick (info) {
      info.jsEvent.preventDefault()
      this.$bus.$emit('modal-event-info', {
        event: info.event,
        callback: newStatus => {
          this.apiEventSetStatus({uuid: info.event.id, status: newStatus})
            .then(() => {
              this.getEvents()
            })
            .catch(errorCatch('Event Status change failed', this))
        }
      })
    },
    refreshClick () {
      this.getEvents()
    },
    eventCreate () {
      this.$bus.$emit(
        'modal-event-edit',
        {
          callback: data => {
            this.apiEventCreate(data)
              .then(() => {
                this.$notify.success({
                  title: 'Event created.',
                  message: '',
                  position: 'top right',
                  timeOut: 5000,
                  closeButton: true
                })
                this.getEvents()
              })
              .catch(errorCatch('Event Create failed', this))
          }
        }
      )
    },
    getEvents (params = null) {
      if (params === null) {
        params = {
          from: this.api.view.activeStart.toISOString(),
          to: this.api.view.activeEnd.toISOString(),
          deadlineBefore: this.deadlineBefore
        }
      }
      if (!('types' in params)) {
        params.types = Object.keys(this.selectedEventTypes).filter(code => this.selectedEventTypes[code])
      }
      if (!('statuses' in params)) {
        params.statuses = Object.keys(this.selectedEventStatuses).filter(code => this.selectedEventStatuses[code])
      }
      this.apiGetEvents(params)
        .then(events => {
          this.events = events.map(ev => {
            const replacer = function (match, p) {
              return ev.extendedProps[p]
            }
            if (ev.urlTemplate) {
              ev.url = ev.urlTemplate.replace(/\{([a-zA-Z0-9]+)\}/g, replacer)
            }
            delete ev.urlTemplate
            return ev
          })
        })
        .catch(errorCatch('Get Events Failed', this))
    },
    datesSet (dateInfo) {
      if (this.selectedEventTypes.length > 0) {
        this.getEvents({from: dateInfo.startStr, to: dateInfo.endStr})
      }
    }
  },
  mounted () {
    this.apiGetEventStatusesInfo()
      .then(statusesInfo => {
        this.statusesInfo = statusesInfo
        this.selectedEventStatuses = this.statusesInfo.reduce((map, es) => {
          map[es.code] = true
          return map
        }, {})
        this.statusesInfoLoaded = true
      })
      .catch(errorCatch('Get Event Statuses failed!', this))
    this.apiGetEventTypes(true)
      .then(types => {
        this.allEventTypes = types
        this.selectedEventTypes = this.allEventTypes.reduce((map, et) => {
          map[et.code] = true
          return map
        }, {})
        this.eventTypesLoaded = true
      })
      .catch(errorCatch('Get Event Types failed!', this))
    this.$bus.$on('calendar-refresh', () => this.getEvents())
  },
  beforeDestroy () {
    this.$bus.$off('calendar-refresh')
  },
  watch: {
    miscDataLoaded: {
      handler: function () {
        this.getEvents()
      }
    },
    deadlineBefore: {
      handler: function () {
        this.getEvents()
      }
    }
  },
  components: {
    FullCalendar,
    EventEditModal,
    EventInfoModal,
    mdbSwitch,
    mdbDropdown,
    mdbDropdownToggle,
    mdbDropdownMenu,
    mdbInput,
    mdbBtn,
    mdbIcon,
    Datetime,
    CalendarBusinessProcesses
  }
}
</script>

<style lang="sass">
.btn.btn-primary
  padding: .5rem 1.6rem
  font-size: .64rem
  border-radius: 10em
  background-color: rgb(26,172,235)!important

  &:not([disabled]):not(.disabled).active
    background-color: rgb(11,232,183)!important

.event-completed
  opacity: 0.3

.event-rejected
  background: repeating-linear-gradient(135deg, transparent, transparent 10px, rgba(255, 0, 0, 0.4) 10px, rgba(255, 0, 0, 0.4) 20px)

</style>



// WEBPACK FOOTER //
// src/components/Calendar.vue